import { GlLink, GlPopover, GlTooltipDirective, GlTruncateText } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { difference, groupBy, xor } from 'lodash-es';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { __, n__, s__ } from '~/locale';
import WorkItemSidebarDropdownWidget from './shared/work_item_sidebar_dropdown_widget.vue?vue3';
import Tracking from '../../tracking/index.js?vue3';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import getGroupContactsQuery from '~/crm/contacts/components/graphql/get_group_contacts.query.graphql';
import workItemCrmContactsQuery from '~/work_items/graphql/work_item_crm_contacts.query.graphql';
import updateWorkItemCrmContactsMutation from '../graphql/update_work_item_crm_contacts.mutation.graphql';
import { i18n, TRACKING_CATEGORY_SHOW, VIEW_CONTEXT } from '../constants';
import { findCrmContactsWidget, newWorkItemFullPath, newWorkItemId } from '../utils.js?vue3';
export default {
    name: 'WorkItemCrmContacts',
    directives: {
        GlTooltip: GlTooltipDirective
    },
    components: {
        GlLink,
        GlPopover,
        GlTruncateText,
        WorkItemSidebarDropdownWidget
    },
    mixins: [
        glFeatureFlagsMixin(),
        Tracking.mixin()
    ],
    inject: {
        viewContext: {
            default: VIEW_CONTEXT.fullScreen
        }
    },
    props: {
        fullPath: {
            type: String,
            required: true
        },
        workItemId: {
            type: String,
            required: true
        },
        workItemIid: {
            type: String,
            required: true
        },
        workItemType: {
            type: String,
            required: true
        }
    },
    emits: [
        'error',
        'update-widget-draft'
    ],
    data () {
        return {
            searchTerm: '',
            searchStarted: false,
            updateInProgress: false,
            selectedCount: 0
        };
    },
    computed: {
        createFlow () {
            return this.workItemId === newWorkItemId(this.workItemType);
        },
        selectedItems () {
            return this.workItemCrmContacts?.contacts?.nodes || [];
        },
        isLoading () {
            return this.$apollo.queries.searchItems.loading;
        },
        selectedItemIds () {
            return this.selectedItems.map(({ id })=>id);
        },
        listItems () {
            const contacts = this.searchItems || [];
            const organizations = this.groupByOrganization(contacts, true);
            return organizations.map(([key, values])=>{
                return {
                    text: key,
                    options: values.map((contact)=>{
                        return {
                            value: contact.id,
                            text: `${contact.firstName} ${contact.lastName}`
                        };
                    })
                };
            });
        },
        // eslint-disable-next-line vue/no-unused-properties
        tracking () {
            return {
                category: TRACKING_CATEGORY_SHOW,
                label: 'item_contact',
                property: `type_${this.workItemType}`,
                extra: {
                    viewContext: this.viewContext
                }
            };
        },
        selectedOrganizations () {
            return this.groupByOrganization(this.selectedItems, false);
        },
        workItemCrmContacts () {
            return findCrmContactsWidget(this.workItem);
        },
        workItemFullPath () {
            return this.createFlow ? newWorkItemFullPath(this.fullPath, this.workItemType) : this.fullPath;
        },
        canUpdateMetadata () {
            return this.workItem?.userPermissions?.setWorkItemMetadata;
        },
        dropdownLabelText () {
            return n__('%d contact', '%d contacts', this.selectedCount);
        }
    },
    apollo: {
        // eslint-disable-next-line @gitlab/vue-no-undef-apollo-properties
        searchItems: {
            query: getGroupContactsQuery,
            variables () {
                return {
                    groupFullPath: this.fullPath.split('/')[0],
                    searchTerm: this.searchTerm
                };
            },
            skip () {
                return !this.searchStarted;
            },
            update (data) {
                return data.group?.contacts?.nodes.filter((contact)=>contact.active || this.selectedItemIds.includes(contact.id));
            },
            error () {
                this.$emit('error', s__('WorkItem|Something went wrong when fetching CRM contacts. Please try again.'));
            }
        },
        // eslint-disable-next-line @gitlab/vue-no-undef-apollo-properties
        workItem: {
            query: workItemCrmContactsQuery,
            variables () {
                return {
                    fullPath: this.workItemFullPath,
                    iid: this.workItemIid,
                    useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField)
                };
            },
            update (data) {
                return data?.namespace?.workItem ?? {};
            },
            result () {
                this.selectedCount = this.selectedItemIds.length;
            },
            skip () {
                return !this.workItemIid;
            },
            error (error) {
                Sentry.captureException(error);
                this.$emit('error', s__('WorkItem|Something went wrong when fetching CRM contacts. Please try again.'));
            }
        }
    },
    methods: {
        groupByOrganization (unsortedContacts, separateSelectedContacts) {
            // Sort the contacts first
            const contacts = [
                ...unsortedContacts
            ].sort((a, b)=>{
                if (a.firstName !== b.firstName) {
                    return a.firstName.localeCompare(b.firstName);
                }
                return a.lastName.localeCompare(b.lastName);
            });
            let groups = [];
            let remainingContacts;
            // Group the selected contacts first
            if (separateSelectedContacts && this.selectedItems.length) {
                remainingContacts = contacts.filter((contact)=>!this.selectedItemIds.includes(contact.id));
                groups.push([
                    __('Selected'),
                    contacts.filter((contact)=>this.selectedItemIds.includes(contact.id))
                ]);
            } else {
                remainingContacts = contacts;
            }
            const orphanContacts = remainingContacts.filter(({ organization })=>!organization);
            // Display each organization and their contacts next
            remainingContacts = remainingContacts.filter(({ organization })=>organization);
            const organizationGroups = Object.entries(groupBy(remainingContacts, 'organization.name'));
            organizationGroups.sort((a, b)=>a[0].localeCompare(b[0]));
            groups = groups.concat(organizationGroups);
            // Then finally display contacts without an organization
            if (orphanContacts.length) {
                groups.push([
                    s__('Crm|No organization'),
                    orphanContacts
                ]);
            }
            return groups;
        },
        search (searchTerm = '') {
            this.searchTerm = searchTerm;
            this.searchStarted = true;
        },
        async updateItems (newSelectedItemIds) {
            this.updateInProgress = true;
            let newSelectedItems;
            const differingItems = xor(newSelectedItemIds, this.selectedItemIds);
            if (differingItems === 0) return;
            if (newSelectedItemIds.length === 0) {
                newSelectedItems = [];
            } else {
                const removeIds = difference(this.selectedItemIds, newSelectedItemIds);
                if (removeIds.length > 0) {
                    newSelectedItems = this.selectedItems.filter(({ id })=>!removeIds.includes(id));
                }
                const addIds = difference(newSelectedItemIds, this.selectedItemIds);
                if (addIds.length > 0) {
                    newSelectedItems = [
                        ...this.selectedItems,
                        ...this.searchItems.filter(({ id })=>addIds.includes(id))
                    ];
                }
            }
            if (this.createFlow) {
                this.$emit('update-widget-draft', {
                    crmContacts: newSelectedItems
                });
                this.updateInProgress = false;
                return;
            }
            try {
                const { data: { workItemUpdate: { errors } } } = await this.$apollo.mutate({
                    mutation: updateWorkItemCrmContactsMutation,
                    variables: {
                        input: {
                            id: this.workItemId,
                            crmContactsWidget: {
                                contactIds: newSelectedItemIds
                            }
                        },
                        useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField)
                    }
                });
                if (errors.length > 0) {
                    throw new Error(errors[0].message);
                }
                this.track('updated_contacts');
            } catch  {
                this.$emit('error', i18n.updateError);
            } finally{
                this.searchTerm = '';
                this.updateInProgress = false;
            }
        },
        rateText (rate) {
            return `${s__('Crm|Rate')}: ${rate}`;
        },
        updateCount (items) {
            this.selectedCount = items.length;
        }
    },
    truncateTextToggleButtonProps: {
        class: '!gl-text-sm'
    }
};
